class StoreUser {
  constructor (
    id = undefined,
    firstName,
    lastName,
    email,
    identifier,
    phoneNumber,
    password,
    familyId,
    languageId,
    roleId,
    photoId,
    areFormsRequired,
    is2FARequired,
    isActive,
    isDeleted,
    deviceIds,
    hasPassword,
    authCodePrefix,
    authCodeDigits,
    authCodeGeneratedAt
  ) {
    this.id = id
    this.firstName = firstName
    this.lastName = lastName
    this.email = email
    this.identifier = identifier
    this.phoneNumber = phoneNumber
    this.password = password
    this.familyId = familyId
    this.family = null
    this.languageId = languageId
    this.language = null
    this.roleId = roleId
    this.role = null
    this.photoId = photoId
    this.photo = null
    this.areFormsRequired = areFormsRequired
    this.is2FARequired = is2FARequired
    this.isActive = isActive
    this.isDeleted = isDeleted === true
    this.deviceIds = deviceIds
    this.devices = null
    this.hasPassword = hasPassword
    this.authCodePrefix = authCodePrefix
    this.authCodeDigits = authCodeDigits
    this.authCodeGeneratedAt = authCodeGeneratedAt
  }

  static toPhotoPatch (photoId) {
    if (photoId == null || photoId === '') {
      return {}
    }
    return { photoId }
  }

  static toPolicyPatch ({ areFormsRequired, is2FARequired } = {}) {
    const patch = {}
    if (typeof areFormsRequired === 'boolean') {
      patch.areFormFillsMandatory = areFormsRequired
    }
    if (typeof is2FARequired === 'boolean') {
      patch.twoFactorAuthEnabled = is2FARequired
    }
    return patch
  }

  toNormalizedRESTModel () {
    const payload = {
      // id is read-only
      firstName: this.firstName,
      lastName: this.lastName,
      email: this.email,
      userName: this.identifier,
      mobilePhone: this.phoneNumber,
      familyId: this.familyId,
      languageId: this.languageId,
      roleId: this.roleId,
      areFormFillsMandatory: this.areFormsRequired,
      twoFactorAuthEnabled: this.is2FARequired,
      isActive: this.isActive
      // deviceIds is read-only
    }
    // photoId is optional; only include when set (never send explicit null — some APIs reject it)
    if (this.photoId != null && this.photoId !== '') {
      payload.photoId = this.photoId
    }
    // Password is set only on create via dedicated create flow, not on user update
    if (this.password && String(this.password).trim()) {
      payload.password = this.password
    }
    if (this.languageId != null && this.languageId !== '') {
      payload.languageId = Number(this.languageId)
    }
    // Omit null, undefined, and empty string so PUT bodies do not send e.g. "photoId": null
    return Object.fromEntries(
      Object.entries(payload).filter(([, v]) => v != null && v !== '' || typeof v === 'boolean')
    )
  }

  static FromNormalizedRESTModel ({
    id,
    firstName,
    lastName,
    email,
    userName,
    mobilePhone,
    familyId,
    languageId,
    roleId,
    photoId,
    areFormFillsMandatory,
    twoFactorAuthEnabled,
    isActive,
    isDeleted,
    deviceIds,
    hasPassword,
    authCodePrefix,
    authCodeDigits,
    authCodeGeneratedAt
  }) {
    const resolvedHasPassword = typeof hasPassword === 'boolean' ? hasPassword : undefined

    return new StoreUser(
      id,
      firstName,
      lastName,
      email,
      userName,
      mobilePhone,
      undefined,
      familyId,
      languageId,
      roleId,
      photoId,
      areFormFillsMandatory,
      twoFactorAuthEnabled,
      isActive,
      isDeleted,
      deviceIds,
      resolvedHasPassword,
      authCodePrefix,
      authCodeDigits,
      authCodeGeneratedAt
    )
  }

  toJSON () {
    return {
      id: this.id,
      firstName: this.firstName,
      lastName: this.lastName,
      email: this.email,
      identifier: this.identifier,
      phoneNumber: this.phoneNumber,
      password: this.password,
      familyId: this.familyId,
      languageId: this.languageId,
      roleId: this.roleId,
      photoId: this.photoId,
      areFormsRequired: this.areFormsRequired,
      is2FARequired: this.is2FARequired,
      isActive: this.isActive,
      isDeleted: this.isDeleted,
      deviceIds: this.deviceIds,
      hasPassword: this.hasPassword,
      authCodePrefix: this.authCodePrefix,
      authCodeDigits: this.authCodeDigits,
      authCodeGeneratedAt: this.authCodeGeneratedAt
    }
  }

  static FromJSON (json) {
    if (!json || typeof json !== 'object') {
      return null
    }

    const {
      id,
      firstName,
      lastName,
      email,
      identifier,
      phoneNumber,
      password,
      familyId,
      languageId,
      roleId,
      photoId,
      areFormsRequired,
      areFormFillsMandatory,
      is2FARequired,
      twoFactorAuthEnabled,
      isActive,
      isDeleted,
      deviceIds,
      hasPassword,
      authCodePrefix,
      authCodeDigits,
      authCodeGeneratedAt
    } = json

    return new StoreUser(
      id,
      firstName,
      lastName,
      email,
      identifier,
      phoneNumber,
      password,
      familyId,
      languageId,
      roleId,
      photoId,
      typeof areFormsRequired === 'boolean' ? areFormsRequired : areFormFillsMandatory,
      typeof is2FARequired === 'boolean' ? is2FARequired : twoFactorAuthEnabled,
      isActive,
      isDeleted,
      deviceIds,
      hasPassword,
      authCodePrefix,
      authCodeDigits,
      authCodeGeneratedAt
    )
  }

  getFullName () {
    return `${this.firstName || ''} ${this.lastName || ''}`
  }
}

export default StoreUser
